import { useState } from "react";
import Modal from "@/components/common/Modal";
import { useApp } from "@/contexts/AppContext";
import { AnalyticsFiltersDrawer } from "@/features/analytics/components/AnalyticsFiltersDrawer";
import {
  cloneAnalyticsFilters,
  createEmptyAnalyticsFilters,
  countActiveAnalyticsFilters,
} from "@/features/analytics/utils/analytics";

const filtersToPredefinedFilter = (filters) => {
  const payload = {};
  for (const key of Object.keys(filters)) {
    const value = filters[key];
    if (Array.isArray(value) && value.length > 0) {
      payload[key] = [...value];
    }
  }
  return payload;
};

// The provider's scope is one JSON object: the device-filter groups the
// drawer edits, plus the publisher endpoints linked to the login (a plain
// list the drawer does not know about). Merge both on the way out.
const withProviderUuids = (predefined, providerUuids) =>
  providerUuids.length > 0
    ? { ...predefined, providerUuid: [...providerUuids] }
    : predefined;

export default function AddUserModal({ isOpen, onClose }) {
  const {
    addUser,
    campaigns,
    availableChannels,
    availableCategories,
    providers = [],
  } = useApp();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("user");
  const [predefinedFilters, setPredefinedFilters] = useState(
    createEmptyAnalyticsFilters(),
  );
  const [draftFilters, setDraftFilters] = useState(
    createEmptyAnalyticsFilters(),
  );
  const [providerUuids, setProviderUuids] = useState([]);
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const resetForm = () => {
    setUsername("");
    setPassword("");
    setRole("user");
    setPredefinedFilters(createEmptyAnalyticsFilters());
    setDraftFilters(createEmptyAnalyticsFilters());
    setProviderUuids([]);
    setError("");
  };

  const handleSubmit = async () => {
    if (!username || !password) {
      setError("Username and password are required");
      return;
    }

    setError("");
    setLoading(true);

    try {
      const predefined =
        role === "provider"
          ? withProviderUuids(
              filtersToPredefinedFilter(predefinedFilters),
              providerUuids,
            )
          : null;
      await addUser(username, password, role, predefined);
      resetForm();
      onClose();
    } catch (err) {
      setError(err.message || "Failed to create user");
    } finally {
      setLoading(false);
    }
  };

  const activeScopeCount = countActiveAnalyticsFilters(predefinedFilters);

  const footer = (
    <>
      <button
        className="btn btn-secondary"
        type="button"
        onClick={() => {
          resetForm();
          onClose();
        }}
        disabled={loading}
      >
        Cancel
      </button>
      <button
        className="btn btn-primary"
        type="button"
        onClick={handleSubmit}
        disabled={loading}
      >
        {loading ? "Creating..." : "Create User"}
      </button>
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        resetForm();
        onClose();
      }}
      title="Create New User"
      footer={footer}
    >
      {error && (
        <div className="form-error" style={{ marginBottom: "1rem" }}>
          {error}
        </div>
      )}
      <div className="form-group-modal">
        <label htmlFor="new-username">Username *</label>
        <input
          id="new-username"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          placeholder="Enter username"
          autoComplete="username"
          disabled={loading}
        />
      </div>
      <div className="form-group-modal">
        <label htmlFor="new-password">Password *</label>
        <input
          id="new-password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Enter password"
          autoComplete="new-password"
          disabled={loading}
        />
      </div>
      <div className="form-group-modal">
        <label htmlFor="new-role">Role *</label>
        <select
          id="new-role"
          value={role}
          onChange={(e) => setRole(e.target.value)}
          disabled={loading}
        >
          <option value="user">User</option>
          <option value="admin">Admin</option>
          <option value="provider">Provider</option>
        </select>
      </div>
      {role === "provider" ? (
        <div className="form-group-modal">
          <label>Predefined scope (provider sees only matching data)</label>
          <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => {
                setDraftFilters(cloneAnalyticsFilters(predefinedFilters));
                setIsFiltersOpen(true);
              }}
              disabled={loading}
            >
              Edit scope
            </button>
            <span
              style={{ fontSize: "0.85rem", color: "var(--text-secondary)" }}
            >
              {activeScopeCount > 0
                ? `${activeScopeCount} filter group(s) set`
                : "No scope set — provider will see nothing until scope is set"}
            </span>
          </div>
        </div>
      ) : null}

      {role === "provider" ? (
        <div className="form-group-modal">
          <label>
            Publisher endpoints (what the Earnings page pays out on; the Devices
            list is limited to their traffic)
          </label>
          {providers.length === 0 ? (
            <span
              style={{ fontSize: "0.85rem", color: "var(--text-secondary)" }}
            >
              No publishers defined yet.
            </span>
          ) : (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "0.35rem",
                maxHeight: "12rem",
                overflowY: "auto",
              }}
            >
              {providers.map((p) => (
                <label
                  key={p.uuid}
                  style={{
                    display: "flex",
                    gap: "0.5rem",
                    alignItems: "center",
                    fontWeight: 400,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={providerUuids.includes(p.uuid)}
                    onChange={(e) =>
                      setProviderUuids((prev) =>
                        e.target.checked
                          ? [...prev, p.uuid]
                          : prev.filter((id) => id !== p.uuid),
                      )
                    }
                    disabled={loading}
                  />
                  <span>
                    {p.name}
                    {p.alias ? (
                      <span style={{ color: "var(--text-secondary)" }}>
                        {" "}
                        ({p.alias})
                      </span>
                    ) : null}
                  </span>
                </label>
              ))}
            </div>
          )}
          <span style={{ fontSize: "0.85rem", color: "var(--text-secondary)" }}>
            {providerUuids.length > 0
              ? `${providerUuids.length} endpoint(s) linked`
              : "None linked — the publisher will see no earnings"}
          </span>
        </div>
      ) : null}

      <AnalyticsFiltersDrawer
        isOpen={isFiltersOpen}
        value={draftFilters}
        campaigns={campaigns || []}
        channels={availableChannels || []}
        categories={availableCategories || []}
        onChange={setDraftFilters}
        onClose={() => setIsFiltersOpen(false)}
        onApply={() => {
          setPredefinedFilters(cloneAnalyticsFilters(draftFilters));
          setIsFiltersOpen(false);
        }}
        onReset={() => setDraftFilters(createEmptyAnalyticsFilters())}
      />
    </Modal>
  );
}
